<template>
  <v-card tile>
    <v-card-title class="pa-4">
      <!-- <v-col cols="12" class="pa-0 ma-0"> -->
      <v-row class="pa-0 ma-0" justify="space-between">
        <h2>{{ title }}</h2>
        <span class="text-section-title">{{ subtitle }}</span>
      </v-row>
      <!-- </v-col> -->
    </v-card-title>
    <v-divider></v-divider>
    <v-card-text class="px-4">
      <slot
        v-bind:editedItem="editedItem"
        v-bind:extraData="extraData"
        v-bind:formStatus="formStatus"
      ></slot>
    </v-card-text>
    <v-card-actions class="pa-4">
      <v-spacer></v-spacer>
      <v-btn icon @click="deleteItem" v-if="editedItem.id"
        ><v-icon>mdi-delete</v-icon></v-btn
      >
      <v-btn
        tile
        :disabled="submitting || loading"
        @click="closePopup(true)"
        elevation="0"
        class="text-none button btn-cancel"
        >{{ cancelBtnTitle }}</v-btn
      >

      <v-btn
        tile
        :disabled="!canSubmitInternal"
        color="primary"
        elevation="0"
        :loading="submitting || loading"
        @click="submit()"
        class="text-none button"
        >{{ saveBtnTitle }}</v-btn
      >
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { db } from '@/firebaseConfig'
import { objects } from '@/helpers'
export default {
  name: 'CommonEditingPopup',
  props: {
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
    },
    originalItem: Object,
    successMessage: {
      type: String,
      default: 'Item successfully updated',
    },
    showDefaultSuccessMsg: {
      type: Boolean,
      default: true,
    },
    errorMessage: String,
    canSubmit: {
      type: Boolean,
      default: undefined,
    },
    dataBasePath: {
      type: String,
    },
    collectionName: {
      type: String,
    },
    /**
     * Use this prop to set spinners in the component from the parent
     */
    loading: {
      type: Boolean,
      default: false,
    },
    saveBtnTitle: {
      type: String,
      default: 'Save',
    },
    cancelBtnTitle: {
      type: String,
      default: 'Cancel',
    },
    ///  If true - will use `dbRef.set(data)` instead of `dbRef.update(data)` in default submit
    useSetForData: {
      type: Boolean,
      default: false,
    },
    /// Use this to provide default value for "new" items
    defaultItem: {
      type: Object,
      default: () => {},
    },
    deleteConfirmationTitle: {
      type: String,
      default: 'Please confirm',
    },
    deleteConfirmationMessage: {
      type: String,
      default: 'Are you sure you want to delete this item?',
    },
  },

  data() {
    const editedItem = Object.assign({}, this.originalItem ?? this.defaultItem)
    if (this.originalItem?.id) {
      Object.defineProperty(editedItem, 'id', { value: this.originalItem?.id })
    }

    return {
      submitting: false,
      editedItem,
      extraData: {},
      formStatus: {
        valid: false,
      },
    }
  },

  computed: {
    ...mapGetters(['companyId']),
    canSubmitInternal() {
      return this.canSubmit === undefined
        ? this.formStatus.valid
        : this.canSubmit
    },

    /**
     Base path for collection.
     Can be overriden with `dataBasePath` property (in case you need a subcollection)
     */
    _basePath() {
      return this.dataBasePath ?? `companies/${this.companyId}`
    },
    /**
      Use for full path to the firestore collection
     */
    fullPath() {
      return this._basePath + '/' + this.collectionName
    },
    hasSubmitListener() {
      return this.$listeners && this.$listeners.submit
    },
    hasDeleteItemListener() {
      return this.$listeners && this.$listeners.deleteItem
    },
    hasCloseListener() {
      return this.$listeners && this.$listeners.close
    },
  },
  methods: {
    ...mapActions([
      'showError',
      'showSuccess',
      'hideNewTableItemPopup',
      'showDeletionSuccess',
    ]),

    getSuccessMessage() {
      // eslint-disable-next-line no-useless-escape
      let regex = /{{\s*([\w\.]+)\s*}}/
      let match = regex.exec(this.successMessage)
      if (match && match.length > 1) {
        return this.successMessage.replace(regex, this.editedItem[match[1]])
      } else {
        return this.successMessage
      }
    },
    /**
     * Default submit function
     */
    async submitDefault() {
      this.submitting = true
      const collectionRef = db.collection(this.fullPath)
      try {
        if (this.editedItem.id != null) {
          // Updating existing item
          const docRef = collectionRef.doc(this.editedItem.id)
          const clone = Object.assign({}, this.editedItem)
          delete clone.id

          if (this.useSetForData) {
            await docRef.set(objects.withUpdatedDataInfo(clone))
          } else {
            await docRef.update(objects.withUpdatedDataInfo(clone))
          }

          if (this.showDefaultSuccessMsg) {
            this.showSuccess(this.getSuccessMessage())
          }

          this.$emit('updated:item', this.editedItem)
        } else {
          const res = await collectionRef.add(
            objects.withCreatedDataInfo(this.editedItem)
          )
          if (this.showDefaultSuccessMsg) {
            this.showSuccess(this.getSuccessMessage())
          }

          Object.defineProperty(this.editedItem, 'id', {
            value: res.id,
          })
          this.$emit('created:item', this.editedItem)
        }
        this.closePopup()
      } catch (error) {
        this.showError(
          this.errorMessage ?? '<b>Failed</b>\nPlease try again later'
        )
        console.error(error)
      } finally {
        this.submitting = false
      }
    },
    async deleteItemDefault() {
      this.submitting = true
      try {
        const collectionRef = db.collection(this.fullPath)
        const docRef = collectionRef.doc(this.editedItem.id)
        await docRef.delete()
        this.showDeletionSuccess('<b>Deleted</b>')
        this.closePopup()
      } catch (error) {
        this.showError(
          this.errorMessage ?? '<b>Failed.</b> Please try again later'
        )
        console.error(error.message)
      } finally {
        this.submitting = false
      }
    },
    async deleteItem() {
      const result = await this.$confirm(this.deleteConfirmationMessage, {
        title: this.deleteConfirmationTitle,
      })

      if (result === true) {
        if (this.hasDeleteItemListener) {
          this.$emit('deleteItem', this.editedItem, this.extraData)
        } else {
          this.deleteItemDefault()
        }
      }
    },
    /**
     * Submit function will either emit `submit` event (if there is listener)
     * or use `submitDefault` function
     */
    async submit() {
      if (this.hasSubmitListener) {
        this.$emit('submit', this.editedItem, this.extraData)
      } else {
        this.submitDefault()
      }
    },
    closePopup(cancelled = false) {
      if (this.hasCloseListener) {
        this.$emit('close', cancelled)
      } else {
        this.hideNewTableItemPopup()
      }
    },
  },
}
</script>

<style lang="sass" scoped>
.btn-cancel
  background-color: $inactive !important
</style>
